import React, { useCallback, useEffect, useRef, useState } from 'react';
import { ChevronLeftIcon, ChevronRightIcon } from 'lucide-react';
import { CtaButton } from './CtaButton';
import { PageLightbox } from './PageLightbox';
import { PageMock } from './PageMock';
import { lookInsidePages } from '../data/lookInsidePages';
export function LookInside() {
  const trackRef = useRef<HTMLUListElement>(null);
  const [active, setActive] = useState(0);
  const [atStart, setAtStart] = useState(true);
  const [atEnd, setAtEnd] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState<number | null>(null);
  const getStep = () => {
    const track = trackRef.current;
    const first = track?.children[0] as HTMLElement | undefined;
    if (!track || !first) return 0;
    const gap = parseFloat(getComputedStyle(track).columnGap) || 0;
    return first.offsetWidth + gap;
  };
  const updateState = useCallback(() => {
    const track = trackRef.current;
    if (!track) return;
    const step = getStep();
    const maxScroll = track.scrollWidth - track.clientWidth;
    setAtStart(track.scrollLeft <= 4);
    setAtEnd(track.scrollLeft >= maxScroll - 4);
    if (step > 0) {
      const idx = track.scrollLeft >= maxScroll - 4 ? lookInsidePages.length - 1 : Math.round(track.scrollLeft / step);
      setActive(Math.min(idx, lookInsidePages.length - 1));
    }
  }, []);
  useEffect(() => {
    updateState();
    window.addEventListener('resize', updateState);
    return () => window.removeEventListener('resize', updateState);
  }, [updateState]);
  const scrollByCards = (dir: 1 | -1) => {
    trackRef.current?.scrollBy({
      left: dir * getStep(),
      behavior: 'smooth'
    });
  };
  const scrollToIndex = (i: number) => {
    trackRef.current?.scrollTo({
      left: i * getStep(),
      behavior: 'smooth'
    });
  };
  return <section aria-labelledby="look-inside-heading" className="w-full bg-ice text-ink" data-id="element-162" data-mp="34qb5llmxj9zj">
      <div className="max-w-6xl mx-auto px-5 py-16 sm:py-20" data-id="element-163" data-mp="2zsrqykmbkv72">
        <div className="text-center" data-id="element-164" data-mp="13ov5hyl9gp8v">
          <p className="text-[11px] font-semibold uppercase tracking-[0.2em] text-amber-deep" data-id="element-165" data-mp="13nsfrtzocsuj">
            Look inside
          </p>
          <h2 id="look-inside-heading" className="mt-3 font-display font-bold text-rink text-3xl sm:text-5xl" data-id="element-166" data-mp="18m3r695mbbev">
            Open the week. Set three lanes. Coach.
          </h2>
          <p className="mx-auto mt-4 max-w-[60ch] text-base text-ink" data-id="element-167" data-mp="1hicrrr5c0g2l">
            Every lesson is a Lesson Card, a 3-Lane Circuit and an 8-count
            Finish — written in the language of the sport: edges, crossovers,
            three-turns, spins.
          </p>
        </div>

        <div className="relative mt-10" data-id="element-168" data-mp="tpclxj99j5hc">
          <ul ref={trackRef} onScroll={updateState} className="-mx-5 flex snap-x snap-mandatory gap-4 overflow-x-auto scroll-px-5 px-5 pb-4 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden md:mx-0 md:gap-6 md:scroll-px-0 md:px-0" data-id="element-169" data-mp="ypilrmporyai">
            {lookInsidePages.map((page, i) => <li key={page.caption} className="w-[85%] shrink-0 snap-start md:w-[calc((100%-1.5rem)/2)]" data-id="element-170" data-mp="2k7j7fntifiww" data-mpi={i}>
                <figure data-id="element-171" data-mp="2aj1bbjw6dzzl">
                  <button type="button" onClick={() => setLightboxIndex(i)} aria-label={`Open full size: ${page.caption}`} className="block w-full overflow-hidden rounded-2xl border border-ice-dark bg-white shadow-card transition-transform duration-200 ease-out hover:-translate-y-0.5 focus:outline-none focus-visible:ring-4 focus-visible:ring-amber/50" data-id="element-172" data-mp="2ai9gibaek6ad">
                    {page.src ? <img src={page.src} alt={page.caption} loading="lazy" width={1600} height={1000} className="aspect-[16/10] w-full object-contain" data-id="element-173" data-mp="1nj3fxm6uazh5" /> : <PageMock kind={page.mock} data-id="element-174" data-mp="3p0m28kn2tlsf" />}
                  </button>
                  <figcaption className="mt-3 text-center text-sm text-ink-soft" data-id="element-175" data-mp="3ty4f2m47sl5p">
                    {page.caption}
                  </figcaption>
                </figure>
              </li>)}
          </ul>

          <button type="button" onClick={() => scrollByCards(-1)} disabled={atStart} aria-label="Previous pages" className="absolute -left-5 top-[calc(50%-1.5rem)] hidden h-12 w-12 -translate-y-1/2 items-center justify-center rounded-full bg-rink text-white shadow-card transition-[background-color,opacity] duration-150 hover:bg-rink-mid focus:outline-none focus-visible:ring-4 focus-visible:ring-amber/50 disabled:pointer-events-none disabled:opacity-30 md:flex lg:-left-6" data-id="element-176" data-mp="3k2kl4597nwjl">
            <ChevronLeftIcon className="h-6 w-6" aria-hidden="true" data-id="element-177" data-mp="2s8ih7juyofxy" />
          </button>
          <button type="button" onClick={() => scrollByCards(1)} disabled={atEnd} aria-label="Next pages" className="absolute -right-5 top-[calc(50%-1.5rem)] hidden h-12 w-12 -translate-y-1/2 items-center justify-center rounded-full bg-rink text-white shadow-card transition-[background-color,opacity] duration-150 hover:bg-rink-mid focus:outline-none focus-visible:ring-4 focus-visible:ring-amber/50 disabled:pointer-events-none disabled:opacity-30 md:flex lg:-right-6" data-id="element-178" data-mp="28myr3twi39ww">
            <ChevronRightIcon className="h-6 w-6" aria-hidden="true" data-id="element-179" data-mp="2mn9kb3la90l8" />
          </button>
        </div>

        <div className="mt-4 flex justify-center gap-2" role="tablist" aria-label="Choose page" data-id="element-180" data-mp="31bmlcez899ml">
          {lookInsidePages.map((page, i) => <button key={page.caption} type="button" role="tab" aria-selected={active === i} aria-label={`Go to page ${i + 1}: ${page.caption}`} onClick={() => scrollToIndex(i)} className="flex h-6 w-6 items-center justify-center focus:outline-none focus-visible:ring-2 focus-visible:ring-amber rounded-full" data-id="element-181" data-mp="3ackoyw5r7f4n" data-mpi={i}>
              <span className={`block h-2 rounded-full transition-[width,background-color] duration-200 ease-out ${active === i ? 'w-6 bg-rink' : 'w-2 bg-glacier-dark'}`} data-id="element-182" data-mp="2c08aemfzhesl" />
            </button>)}
        </div>

        <div className="mt-10 flex flex-col items-center" data-id="element-183" data-mp="2qpe8u86zz0zf">
          <p className="text-sm text-ink-soft" data-id="element-184" data-mp="2qs0hkb5mjmj8">
            6 volumes · 52 lessons · one-time $27
          </p>
          <div className="mt-4 w-full max-w-md" data-id="element-185" data-mp="2zqvppss8b6p6">
            <CtaButton href="#pricing" data-id="element-186" data-mp="2zqc3w0zgjy5v">Get all 52 lessons</CtaButton>
          </div>
        </div>
      </div>

      <PageLightbox pages={lookInsidePages} index={lightboxIndex} onClose={() => setLightboxIndex(null)} onChange={setLightboxIndex} data-id="element-187" data-mp="11d2d8gsfhncv" />
    </section>;
}